/**
 * Cell renderer utilities for SimpleKeywordTabs
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { EnhancedTableColumn } from '@/modules/app/components/ui/table';
import { format, parseISO, isValid } from 'date-fns';
import { isDebugMode, debugLog } from '@/lib/debugUtils';

// Helper function to format dates for trends data (month-year format)
const formatTrendsDate = (dateString?: string, userEmail?: string): string => {
  if (!dateString) return '-';
  
  try {
    // Handle both ISO strings and regular date strings
    let date: Date;
    
    // Try parsing as ISO first (handles timestamps like "2025-05-25T00:00:00+05:30")
    if (dateString.includes('T') || dateString.includes('Z')) {
      date = parseISO(dateString);
    } else {
      // Handle other date formats
      date = new Date(dateString);
    }
    
    if (isValid(date)) {
      // Get day with proper ordinal suffix (1st, 2nd, 3rd, etc.)
      const day = date.getDate();
      const ordinalSuffix = (day: number): string => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
          case 1: return 'st';
          case 2: return 'nd';
          case 3: return 'rd';
          default: return 'th';
        }
      };
      
      // Format as "1st Jan 2025"
      const dayWithSuffix = `${day}${ordinalSuffix(day)}`;
      const monthYear = format(date, 'MMM yyyy');
      return `${dayWithSuffix} ${monthYear}`;
    }
  } catch (error) {
    if (isDebugMode(userEmail)) {
      debugLog(userEmail, 'Error parsing trends date:', dateString, error);
    }
  }
  
  // Fallback: return the original string if parsing fails
  return dateString;
};

// Custom cell renderer function
export const cellRenderer = (
  value: unknown, 
  column: EnhancedTableColumn, 
  row: Record<string, unknown>,
  userEmail?: string
) => {
  switch (column.key) {
    case 'data_type':
      if (value) {
        const sourceLabel = value === 'ideas' ? 'Ideas' : 'Suggestions';
        const badgeClass = value === 'ideas' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                          'bg-green-100 text-green-800 border-green-200';
        
        return (
          <Badge className={`text-xs font-medium ${badgeClass}`}>
            {sourceLabel}
          </Badge>
        );
      }
      return <span className="text-muted-foreground">-</span>;
    case 'url':
      return (
        <a
          href={value as string}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 underline break-all whitespace-normal block"
        >
          {value}
        </a>
      );
    case 'description':
    case 'answer':
      return (
        <div className="max-w-md text-foreground break-words whitespace-normal">
          {value || '-'}
        </div>
      );
    case 'date_from':
    case 'date_to':
      return (
        <span className="text-foreground">
          {formatTrendsDate(value as string, userEmail)}
        </span>
      );
    default:
      return (
        <span className="text-foreground">
          {value || '-'}
        </span>
      );
  }
};